import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import bind from '@reverbdotcom/commons/src/bind';
import FeedbackStore from './feedback_store';
import FeedbackActions from './feedback_actions';
import FeedbackItem from './feedback_item';
import Feedback from '../feedback';
import FeedbackSummary from '../feedback_summary';
import mapStateToProps from '../../map_state_to_props';
import FeedbackSummaryTable from './feedback_summary_table';

import Tiles from '@reverbdotcom/commons/src/components/tiles';

import noFeedbackIconSrc from '../../../../images/icons/no-feedback-icon.png';

interface Props {
  shopSlug: string;
  visibleFeedbacks: string;
  feedbackSummary: FeedbackSummary;
  feedbacks: Feedback[];
  nextFeedbacks: string;
  onLoadMore: Function;
  loadAllFeedback: Function;
  loadSellerFeedback: Function;
  loadBuyerFeedback: Function;
  loaded: boolean;
}

class FeedbackList extends React.Component<Props, null> {
  @bind
  handleViewMore() {
    return (
      this.props.onLoadMore(this.props.nextFeedbacks)
    );
  }

  @bind
  showBuyerFeedbacks() {
    this.props.loadBuyerFeedback(this.props.shopSlug);
  }

  @bind
  showSellerFeedbacks() {
    this.props.loadSellerFeedback(this.props.shopSlug);
  }

  @bind
  showAllFeedbacks() {
    this.props.loadAllFeedback(this.props.shopSlug);
  }

  get feedbacksTotalCount() {
    return this.props.feedbackSummary.total_counts.all;
  }

  renderPlaceholder() {
    return (
      <div
        className="opacity-50 bg-white width-100"
        style={{ height: '10rem' }}
      />
    );
  }

  renderFeedbackList() {
    return (
      <Tiles
        oneWide
        loading={!this.props.loaded}
        emptyState={this.renderEmptyState()}
        placeholder={this.renderPlaceholder()}
        expectedCount={3}
      >
        {this.props.feedbacks.map(this.renderFeedback)}
      </Tiles>
    );
  }

  renderFeedback(feedback, index) {
    return (
      <FeedbackItem
        feedback={feedback}
        key={index}
      />
    );
  }

  renderFilters() {
    if (!this.props.feedbackSummary) return;
    if (this.feedbacksTotalCount === 0) return;

    /* disabling inline here so we can enable this rule globally, but this should be fixed the next time this file is touched */
    /* eslint-disable jsx-a11y/anchor-is-valid */
    return (
      <div className="input-group">
        <a
          className={
            classNames(
              'input-group__button',
              { 'opacity-60': (this.props.visibleFeedbacks !== 'all') },
            )
          }
          onClick={this.showAllFeedbacks}
        >
          {I18n.t(
            'discovery.shop.feedback.all_count',
            { count: this.props.feedbackSummary.total_counts_formatted.all },
          )}
        </a>
        <a
          className={
            classNames(
              'input-group__button',
              { 'opacity-60': (this.props.visibleFeedbacks !== 'seller') },
            )
          }
          onClick={this.showSellerFeedbacks}
        >
          {I18n.t(
            'discovery.shop.feedback.seller_count',
            { count: this.props.feedbackSummary.total_counts_formatted.seller },
          )}
        </a>
        <a
          className={
            classNames(
              'input-group__button',
              { 'opacity-60': (this.props.visibleFeedbacks !== 'buyer') },
            )
          }
          onClick={this.showBuyerFeedbacks}
        >
          {I18n.t(
            'discovery.shop.feedback.buyer_count',
            { count: this.props.feedbackSummary.total_counts_formatted.buyer },
          )}
        </a>
      </div>
    );
  }

  renderFeedbackSummaryTable() {
    if (!this.props.feedbackSummary) return;
    if (this.feedbacksTotalCount === 0) { return null; }

    return (
      <div className="g-col-4 g-col-tablet-6 g-col-mobile-12 mobile-mb-2">
        <FeedbackSummaryTable />
      </div>
    );
  }

  renderEmptyState() {
    return (
      <div className="scaling-mtb-3 align-center">
        <img
          src={noFeedbackIconSrc}
          alt={I18n.t('discovery.shop.feedback.noFeedbackIcon')}
          className="width-30 tablet-width-50 mobile-width-70 mlr-auto d-block"
        />
        <h4 className="size-120 weight-bold scaling-mt-4">
          {I18n.t('discovery.shop.feedback.noFeedbackHere')}
        </h4>
      </div>
    );
  }

  renderShowMore() {
    if (!this.props.nextFeedbacks) { return null; }

    return (
      <div className="align-center mt-1">
        <a
          className="button"
          onClick={this.handleViewMore}
          data-load-more
        >
          {I18n.t('discovery.shop.feedback.seeMore')}
        </a>
      </div>
    );
  }

  render() {
    return (
      <div className="site-wrapper scaling-mb-2">
        {this.renderFilters()}
        <div className="g-container scaling-mtb-3 mobile-d-flex mobile-fx-wrap">
          <div className="g-col-8 g-col-tablet-6 g-col-mobile-12 mobile-fx-order-2">
            {this.renderFeedbackList()}
            {this.renderShowMore()}
          </div>
          {this.renderFeedbackSummaryTable()}
        </div>
      </div>
    );
  }
}

function mapper() {
  return {
    feedbacks: FeedbackStore.getState().feedbacks,
    visibleFeedbacks: FeedbackStore.getState().visibleFeedbacks,
    nextFeedbacks: FeedbackStore.getState().nextFeedbacks,
    feedbackSummary: FeedbackStore.getState().feedbackSummary,
    onLoadMore: FeedbackActions.loadMore,
    loadAllFeedback: FeedbackActions.loadAllFeedback,
    loadSellerFeedback: FeedbackActions.loadSellerFeedback,
    loadBuyerFeedback: FeedbackActions.loadBuyerFeedback,
    loaded: FeedbackStore.getState().loaded,
  };
}

export default mapStateToProps(
  { mapper, stores: [FeedbackStore] },
)(FeedbackList, 'FeedbackList');
